import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../utils/seo';

const projects = [
  {
    name: 'MultiClip',
    tagline:
      'Chrome extension for managing multiple clipboard entries at once.',
    category: 'Chrome Extension',
    tags: ['JavaScript', 'Chrome API', 'Manifest V3', 'i18n'],
    docsRoute: '/multiclip',
    github: 'https://github.com/Oluwasegun-AA/MultiClip',
    store:
      'https://chrome.google.com/webstore/detail/multiclip/kgifojlpdkhneaaefefmhpijbnmmdpeb',
    storeLabel: 'Chrome Store',
  },
  {
    name: 'md-generator',
    tagline:
      'CLI tool that bootstraps open-source projects with all required Markdown files.',
    category: 'CLI · npm Package',
    tags: ['TypeScript', 'Node.js', 'Commander.js', 'Inquirer.js'],
    docsRoute: '/md-generator',
    github: 'https://github.com/Oluwasegun-AA/md-generator',
    store: 'https://www.npmjs.com/package/md-generator',
    storeLabel: 'npm',
  },
  {
    name: 'react-bird-flock',
    tagline:
      'Animated SVG bird flock React component with cursor-tracking perching behaviour.',
    category: 'React · npm Package',
    tags: ['React', 'TypeScript', 'requestAnimationFrame', 'SVG'],
    docsRoute: '/react-bird-flock',
    github: 'https://github.com/Oluwasegun-AA/react-bird-flock',
    store: 'https://www.npmjs.com/package/react-bird-flock',
    storeLabel: 'npm',
  },
  {
    name: 'Caesar Cipher',
    tagline:
      'Interactive web implementation of the Caesar Cipher encryption algorithm.',
    category: 'Web App',
    tags: ['JavaScript', 'Cryptography', 'Algorithms'],
    docsRoute: null,
    github:
      'https://github.com/Oluwasegun-AA/Caesar_Cipher_Encryption_Algorithm',
    store:
      'https://oluwasegun-aa.github.io/Caesar_Cipher_Encryption_Algorithm/',
    storeLabel: 'Live Demo',
  },
  {
    name: 'Yoruba Language App',
    tagline:
      'A language application for learning and exploring the Yorùbá language.',
    category: 'Android App',
    tags: ['Java', 'Localisation', 'Education'],
    docsRoute: null,
    github: 'https://github.com/Oluwasegun-AA/Yoruba-Language-App',
    store: null,
    storeLabel: null,
  },
];

const organisations = [
  {
    name: 'Andela',
    role: 'Software Engineer',
    desc: 'Global talent marketplace connecting African tech talent with world-class companies.',
    url: 'https://andela.com',
    logo: 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://andela.com&size=256',
  },
  {
    name: 'ProdPerfect',
    role: 'Software Engineer',
    desc: 'Data-driven automated end-to-end testing platform powered by production analytics.',
    url: 'https://prodperfect.com',
    logo: 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://prodperfect.com&size=256',
  },
  {
    name: 'Clinify',
    role: 'Software Engineer',
    desc: 'Healthcare technology startup building tools to improve clinical workflows.',
    url: 'https://www.myclinify.com/',
    logo: 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://myclinify.com&size=256',
  },
  {
    name: 'Topship (YC w22)',
    role: 'Lead Backend Engineer',
    desc: 'African logistics platform simplifying local and international shipments for businesses.',
    url: 'https://topship.africa',
    logo: 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://topship.africa&size=256',
  },
  {
    name: 'Hotel Engine',
    role: 'Software Engineer',
    desc: 'Business travel platform providing negotiated hotel rates for companies and their teams.',
    url: 'https://hotelengine.com',
    logo: 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://hotelengine.com&size=256',
  },
  {
    name: 'One Acre Fund',
    role: 'Senior Software Engineer',
    desc: 'Agricultural development non-profit serving smallholder farmers across Sub-Saharan Africa.',
    url: 'https://oneacrefund.org',
    logo: 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://oneacrefund.org&size=256',
  },
  {
    name: 'Flenjor',
    role: 'Senior Software Engineer',
    desc: 'Social experience curator and marketplace redefining monetization.',
    url: 'https://weflenjor.com',
    logo: 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://weflenjor.com&size=256',
  },
];

const ExternalIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// Falls back to a styled initial when the logo URL fails or is absent
const OrgLogo: React.FC<{ name: string; logo: string | null }> = ({
  name,
  logo,
}) => {
  const [failed, setFailed] = React.useState(false);
  if (!logo || failed) {
    return <span className="work_org_initial">{name[0]}</span>;
  }
  return (
    <img
      src={logo}
      alt={`${name} logo`}
      className="work_org_logo_img"
      onError={() => setFailed(true)}
    />
  );
};

const Work: React.FC = (): ReactElement => (
  <>
    <Seo
      pageTitle="Work & Projects | Oluwasegun Adepoju"
      description="Explore open-source projects, product experiments, and software engineering work by Oluwasegun Adepoju across frontend, backend, tooling, and product/architectural design."
      path="/work"
    />
    <div className="work_page">
      {/* ── Hero ──────────────────────────────────────── */}
      <section className="work_hero">
        <p className="work_eyebrow">Portfolio</p>
        {/* <h1 className="work_title">Explorations</h1> */}
        <p className="work_subtitle">
          Projects I&apos;ve built and organisations I&apos;ve had the privilege
          to work with.
        </p>
      </section>

      <div className="work_body">
        {/* ── Projects ──────────────────────────────── */}
        <section className="work_section">
          <h2 className="work_section_title">Opensource Projects</h2>
          <div className="work_projects_grid">
            {projects.map((p) => (
              <div key={p.name} className="work_project_card">
                <div className="work_project_top">
                  <span className="work_category_chip">{p.category}</span>
                  {p.docsRoute && (
                    <Link className="work_docs_badge" to={p.docsRoute}>
                      Docs →
                    </Link>
                  )}
                </div>
                <h3 className="work_project_name">{p.name}</h3>
                <p className="work_project_tagline">{p.tagline}</p>
                <div className="work_tags">
                  {p.tags.map((t) => (
                    <span key={t} className="work_tag">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="work_project_links">
                  <a
                    href={p.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="work_link"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z" />
                    </svg>
                    GitHub
                  </a>
                  {p.store && (
                    <a
                      href={p.store}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="work_link"
                    >
                      <ExternalIcon />
                      {p.storeLabel}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Organisations ─────────────────────────── */}
        <section className="work_section">
          <h2 className="work_section_title">Experience</h2>
          <p className="work_section_lead">
            Organisations I&apos;ve had the privilege to work with.
          </p>
          <div className="work_orgs_grid">
            {organisations.map((o) => (
              <div key={o.name} className="work_org_card">
                <div className="work_org_header">
                  <div className="work_org_logo">
                    <OrgLogo name={o.name} logo={o.logo} />
                  </div>
                  <div className="work_org_meta">
                    <div className="work_org_name_row">
                      <h3 className="work_org_name">{o.name}</h3>
                      {o.url && (
                        <a
                          href={o.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="work_org_link"
                          aria-label={`${o.name} website`}
                        >
                          <ExternalIcon />
                        </a>
                      )}
                    </div>
                    <span className="work_role_chip">{o.role}</span>
                  </div>
                </div>
                <p className="work_org_desc">{o.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  </>
);

export default Work;
